import React from 'react';
import InfoTooltip from '../ui/InfoTooltip';
import Button from '../ui/Button';
import { useCampaignHubupPublishedNotifications } from '../../hooks/useCampaignHubupPublishedNotifications';
import {
  DEFAULT_HUBUP_NOTIFICATION_BUTTON_TARGET,
  HUBUP_NOTIFICATION_BUTTON_VARIABLE,
  normalizeHubupNotificationButtonColor,
  publishedHubupNotificationsFromCampaign,
} from '../../utils/campaignEmailTemplate';
import { hubupButtonDesignFromSource } from '../../utils/emailButtonStyles';
import EmailButtonDesignFields from './EmailButtonDesignFields';

export function emptyHubupSendState() {
  return {
    messageId: '',
    title: '',
    formId: '',
    buttonTarget: DEFAULT_HUBUP_NOTIFICATION_BUTTON_TARGET,
  };
}

export function hubupSendStateFromSource(source) {
  const messageId = String(source?.hubupEmailNotificationMessageId || '').trim();
  if (!messageId) return emptyHubupSendState();
  return {
    messageId,
    title: String(source?.hubupEmailNotificationTitle || '').trim(),
    formId: String(source?.hubupEmailNotificationFormId || '').trim(),
    buttonTarget:
      source?.hubupEmailNotificationButtonTarget === 'form'
        ? 'form'
        : DEFAULT_HUBUP_NOTIFICATION_BUTTON_TARGET,
  };
}

export function hubupStateToEmailOverride(state) {
  if (!state?.messageId) {
    return { hubupEmailNotificationMessageId: null };
  }
  return {
    hubupEmailNotificationMessageId: state.messageId || null,
    hubupEmailNotificationTitle: state.title || null,
    hubupEmailNotificationFormId: state.formId || null,
    hubupEmailNotificationButtonTarget:
      state.buttonTarget || DEFAULT_HUBUP_NOTIFICATION_BUTTON_TARGET,
  };
}

export function resolveHubupPreviewNotification(
  campaign,
  hubupState,
  designSource,
  publishedList = null,
) {
  if (!hubupState?.messageId) return null;
  const published =
    publishedList || publishedHubupNotificationsFromCampaign(campaign);
  const selected = published.find((x) => x.messageId === hubupState.messageId) || null;
  const formId = hubupState.formId || selected?.formId || '';
  const buttonTarget =
    hubupState.buttonTarget === 'form' && formId
      ? 'form'
      : DEFAULT_HUBUP_NOTIFICATION_BUTTON_TARGET;
  if (!selected) return null;
  const design = hubupButtonDesignFromSource(designSource || campaign);
  return {
    url: selected.url,
    formUrl: selected.formUrl,
    buttonTarget,
    buttonLabel: design.buttonLabel,
    buttonColor: design.buttonColor,
    buttonBorderColor: design.buttonBorderColor,
    buttonTextColor: design.buttonTextColor,
    buttonShape: design.buttonShape,
  };
}

export default function HubupNotificationPickerFields({
  campaign,
  value,
  onChange,
  onInsertVariable,
  className = '',
  showButtonDesign = false,
  buttonDesign,
  onButtonDesignChange,
  required = false,
}) {
  const { published, loading, error, groupId, refetch } =
    useCampaignHubupPublishedNotifications(campaign);
  const selected =
    published.find((item) => item.messageId === value.messageId) || null;
  const formId = value.formId || selected?.formId || '';
  const buttonTarget =
    value.buttonTarget === 'form' && formId
      ? 'form'
      : DEFAULT_HUBUP_NOTIFICATION_BUTTON_TARGET;
  const publicUrl =
    buttonTarget === 'form' && selected?.formUrl
      ? selected.formUrl
      : selected?.url || '';

  return (
    <div className={`space-y-3 rounded-lg border border-border bg-muted/30 p-3 ${className}`}>
      <div>
        <div className="mb-1 flex items-center gap-1.5">
          <label className="text-xs font-medium text-foreground">
            Notification HubUP à lier à cet envoi
          </label>
          <InfoTooltip
            text={
              required
                ? 'Obligatoire : le template contient le bouton HubUP. Les notifications sont chargées depuis la communauté liée à la campagne (HubUP).'
                : 'Liste synchronisée depuis HubUP (communauté liée à la campagne). Sans choix, la variable HubUP ne produit pas de bouton à l’envoi.'
            }
          />
        </div>
        {groupId ? (
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-muted-foreground">
              Source : communauté HubUP liée à la campagne
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              iconName="RefreshCw"
              disabled={loading}
              onClick={refetch}
            >
              Actualiser
            </Button>
          </div>
        ) : null}
        {loading ? (
          <p className="text-sm text-muted-foreground">
            Récupération des notifications depuis HubUP…
          </p>
        ) : error ? (
          <div className="space-y-2">
            <p className="text-sm text-destructive">{error}</p>
            <Button type="button" variant="outline" size="sm" onClick={refetch}>
              Réessayer
            </Button>
          </div>
        ) : published.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {groupId
              ? 'Aucune notification dans cette communauté sur HubUP. Publiez-en une sur HubUP puis cliquez sur Actualiser.'
              : 'Liez d’abord une communauté HubUP via Modifier la campagne : les notifications seront chargées automatiquement depuis HubUP.'}
          </p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-1">
            {!required ? (
              <li>
                <button
                  type="button"
                  onClick={() => onChange(emptyHubupSendState())}
                  className={`w-full rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
                    !value.messageId
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-muted/50'
                  }`}
                >
                  Ne pas lier de notification
                </button>
              </li>
            ) : null}
            {published.map((item) => {
              const active = value.messageId === item.messageId;
              return (
                <li key={item.messageId}>
                  <button
                    type="button"
                    onClick={() =>
                      onChange({
                        ...value,
                        messageId: item.messageId,
                        title: item.title || '',
                        formId: item.formId || '',
                        buttonTarget:
                          item.formId && value.buttonTarget === 'form'
                            ? 'form'
                            : DEFAULT_HUBUP_NOTIFICATION_BUTTON_TARGET,
                      })
                    }
                    className={`w-full rounded-lg border px-3 py-2.5 text-left transition-colors ${
                      active
                        ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <span className="block font-medium text-foreground">
                      {item.title || 'Sans titre'}
                    </span>
                    {item.topicTitle ? (
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {item.topicTitle}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {required && !value.messageId && published.length > 0 ? (
          <p className="text-xs text-destructive">Sélectionnez une notification pour continuer.</p>
        ) : null}
      </div>

      {value.messageId ? (
        <>
          <div>
            <p className="mb-1 text-xs font-medium text-foreground">Cible du bouton</p>
            <div className="inline-flex rounded-md border border-border bg-background p-0.5">
              <button
                type="button"
                className={`rounded px-3 py-1.5 text-xs font-medium ${
                  buttonTarget === 'notification'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground'
                }`}
                onClick={() =>
                  onChange({ ...value, buttonTarget: 'notification' })
                }
              >
                Notification
              </button>
              <button
                type="button"
                className={`rounded px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${
                  buttonTarget === 'form'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground'
                }`}
                onClick={() => onChange({ ...value, buttonTarget: 'form' })}
                disabled={!formId}
              >
                Formulaire
              </button>
            </div>
          </div>
          {publicUrl ? (
            <p className="break-all text-xs text-muted-foreground">URL : {publicUrl}</p>
          ) : null}
        </>
      ) : null}

      {showButtonDesign && onButtonDesignChange ? (
        <EmailButtonDesignFields
          title="Design du bouton HubUP"
          value={buttonDesign}
          onChange={onButtonDesignChange}
        />
      ) : null}

      {onInsertVariable ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          iconName="Link"
          onClick={onInsertVariable}
        >
          Insérer {HUBUP_NOTIFICATION_BUTTON_VARIABLE}
        </Button>
      ) : null}
    </div>
  );
}
